import React from 'react';
import { MetricRecordCaptureParameters } from '../../../views/metric_collection/MetricCollectionDataManagement';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Grid, IconButton, TextField, Theme, Tooltip, useTheme } from '@mui/material';
import ReportingPeriodSelect from '../../shared/input/select/ReportingPeriodSelect';
import {
  Country,
  Division,
  EmissionFactor,
  ExternalCompany,
  MetricRecord,
  Region,
  ReportingPeriod,
  Site,
  Subdivision
} from '@esg/esg-global-types';
import BackspaceIcon from '@mui/icons-material/Backspace';
import { ReactComponent as CreateIcon } from '../../../assets/icons/Create.svg';
import CountrySelect from '../../shared/input/select/CountrySelect';
import RegionSelect from '../../shared/input/select/RegionSelect';
import DivisionSelect from '../../shared/input/select/DivisionSelect';
import SubdivisionSelect from '../../shared/input/select/SubdivisionSelect';
import SiteSelect from '../../shared/input/select/SiteSelect';
import { CaptionText } from '../../shared/typography/PrimaryTypography';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import {
  MetricRecordEntitiesMap,
  MetricRecordReportingEntity,
  QuantitativeMetricRecordInputData,
  createBulkMetricRecord,
  createMetricRecord,
  metric_record_label,
  validQuantitativeMetricRecordProportion,
  validQuantitativeMetricRecordValue
} from '../../../graphql/services/metric_record.service';
import { QuantitativeMetricRecordGridRow } from './QuantitativeMetricRecordGrid';
import { reportingPeriodFilterByGroup } from '../../../lib/metric_capture/reporting_period';
import ActivityOwnerSelect, { ActivityOwner } from '../../shared/input/select/ActivityOwnerSelect';
import { ReactComponent as SearchIcon } from '../../../assets/icons/Search.svg';
import { UserContext } from '../../../context/UserContext';
import { getUserActivityOwners } from '../../../util/user_access';
import { values } from '../../../util/general_utils';
import { MetricExtended } from '../../../lib/metric_capture/metric';
import {
  mapAveragedMetricRecordInputForRequest,
  mapQuantitativeMetricRecordInputForRequest
} from '../../../graphql/utilities/metric_record.util';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import { CreateMetricRecordClientData } from '@esg/syntiro-api-core';

const SOURCE = 'QuantitativeMetricRecordGridToolbar.tsx';

interface QuantitativeMetricRecordGridToolbarProps {
  entities_map: MetricRecordEntitiesMap;
  capture_parameters: MetricRecordCaptureParameters;
  metric_record_rows: Array<QuantitativeMetricRecordGridRow>;
  handleUpdateMetricRecordRows: (
    new_metric_records: Array<QuantitativeMetricRecordGridRow>
  ) => void;
  refreshMetricRecordsGrid: () => void;
}

/**
 * Datagrid toolbar for creating new metric records manually for given parameters.
 * @param {MetricRecordEntitiesMap} entities_map Entities to be used as options for select inputs.
 * @param {MetricRecordCaptureParameters} capture_parameters Parameters of current metric record capturing flow from previous inputs.
 * @returns {JSX.Element}
 */
const QuantitativeMetricRecordGridToolbar = ({
  entities_map,
  capture_parameters,
  metric_record_rows,
  handleUpdateMetricRecordRows,
  refreshMetricRecordsGrid
}: QuantitativeMetricRecordGridToolbarProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  // Validate essential context and parameters
  if (!group || !company || !capture_parameters.metric || !capture_parameters.data_level)
    return null;

  const [reportingPeriod, setReportingPeriod] = React.useState<ReportingPeriod | null>(null);
  const [reportingEntity, setReportingEntity] = React.useState<MetricRecordReportingEntity | null>(
    null
  );
  const [value, setValue] = React.useState<string>('');
  const [allocation, setAllocation] = React.useState<number>(100);
  const [note, setNote] = React.useState<string>('');
  const [activityOwner, setActivityOwner] = React.useState<ActivityOwner | null>(null);
  const [addLoading, setAddLoading] = React.useState<boolean>(false);
  const theme: Theme = useTheme();

  const activity_owner_options: Array<ActivityOwner> =
    user && company && group
      ? getUserActivityOwners(
          user,
          group.id,
          company,
          values<ExternalCompany>(entities_map.external_companies)
        )
      : [];

  const unlocked_reporting_periods = values<ReportingPeriod>(entities_map.reporting_periods).filter(
    (reporting_period: ReportingPeriod) => !reporting_period.locked
  );

  const calculated_metrics = values<MetricExtended>(entities_map.metrics).filter(
    (metric: MetricExtended) =>
      metric.is_calculated &&
      metric.primary_metric_calculated &&
      metric.primary_metric_calculated.id === capture_parameters.metric?.id
  );

  const quantitative_metric_record_input_data: QuantitativeMetricRecordInputData = {
    metric: capture_parameters.metric,
    data_level: capture_parameters.data_level,
    reporting_entity: reportingEntity,
    reporting_period: reportingPeriod,
    value: Number(value),
    emission_factors: values<EmissionFactor>(entities_map.emission_factors),
    external_company: activityOwner?.is_external
      ? entities_map.external_companies[activityOwner.id]
      : null,
    note: note,
    proportion: allocation,
    average_over_year: capture_parameters.average_over_year,
    source: 'manual',
    calculated_metrics: calculated_metrics
  };

  const resetInputs = (): void => {
    setValue('');
    setAllocation(100);
    setNote('');
  };

  // Check that the input data for the new metric record is valid.
  const validateQuantitativeMetricRecord = (): boolean => {
    return Boolean(
      (reportingPeriod || capture_parameters.average_over_year) &&
      reportingEntity &&
      activityOwner &&
      validQuantitativeMetricRecordValue(value) &&
      validQuantitativeMetricRecordProportion(allocation, activityOwner)
    );
  };

  // Create new metric record or averaged metric records in database and add to datagrid in memory.
  const handleAddMetricRecord = async (): Promise<void> => {
    try {
      if (
        group &&
        company &&
        capture_parameters.metric &&
        capture_parameters.data_level &&
        validateQuantitativeMetricRecord()
      ) {
        setAddLoading(true);
        const external_company: ExternalCompany | null | undefined = activityOwner?.is_external
          ? entities_map.external_companies[activityOwner.id]
          : null;
        if (capture_parameters.average_over_year && capture_parameters.reporting_period_group) {
          // Bulk create metric records for each reporting period in chosen reporting period group.
          const create_bulk_metric_record_client_data: Array<CreateMetricRecordClientData> =
            mapAveragedMetricRecordInputForRequest(
              quantitative_metric_record_input_data,
              capture_parameters.reporting_period_group.id,
              unlocked_reporting_periods
            );
          await createBulkMetricRecord(
            { group_id: group.id, company_id: company.id },
            create_bulk_metric_record_client_data
          );
          refreshMetricRecordsGrid();
        } else {
          // Create single metric record for chosen reporting period.
          const create_metric_record_client_data: CreateMetricRecordClientData =
            mapQuantitativeMetricRecordInputForRequest(quantitative_metric_record_input_data);
          const new_metric_record: MetricRecord | null | undefined = await createMetricRecord(
            { group_id: group.id, company_id: company.id },
            create_metric_record_client_data,
            {
              return_data: true,
              create_audit_log: true
            }
          );
          if (new_metric_record && reportingEntity && reportingPeriod) {
            const new_metric_record_row: QuantitativeMetricRecordGridRow = {
              id: new_metric_record.id,
              reporting_period: reportingPeriod,
              reporting_entity: reportingEntity,
              unit: capture_parameters.metric.unit ?? '',
              emission_factor_id: create_metric_record_client_data.emission_factor
                ? create_metric_record_client_data.emission_factor.id
                : undefined,
              value: parseFloat(Number(value).toFixed(10)).toString(),
              proportion: allocation,
              note: note,
              external_company: external_company ?? null,
              docs: false,
              is_averaged: false
            };
            handleUpdateMetricRecordRows([new_metric_record_row, ...metric_record_rows]);
          }
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleAddMetricRecord'),
          {
            group: group,
            company: company,
            capture_parameters: capture_parameters,
            reporting_period: reportingPeriod,
            reporting_entity: reportingEntity,
            value: value,
            allocation: allocation,
            note: note,
            activity_owner: activityOwner
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to add metric record. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setAddLoading(false);
      resetInputs();
    }
  };

  const handleChangeReportingPeriod = (reporting_period: ReportingPeriod | null): void => {
    setReportingPeriod(reporting_period);
  };

  const handleChangeReportingEntity = (
    reporting_entity: MetricRecordReportingEntity | null
  ): void => {
    setReportingEntity(reporting_entity);
  };

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const valid_decimal_number = /^\d*\.?\d*$/;
    if (valid_decimal_number.test(event.target.value)) {
      setValue(event.target.value);
    }
  };

  const handleChangeAllocation = (allocation: number): void => {
    setAllocation(allocation);
  };

  const handleChangeNote = (note: string): void => {
    setNote(note);
  };

  // Update activity owner and reset client allocation if internal company.
  const handleChangeActivityOwner = (activity_owner: ActivityOwner | null): void => {
    if (!activity_owner?.is_external) {
      setAllocation(100);
    }
    setActivityOwner(activity_owner);
  };

  // Set activity owner automatically if internal user or external with 1 external company.
  React.useEffect(() => {
    try {
      if (user) {
        if (activity_owner_options.length === 1) {
          setActivityOwner(activity_owner_options[0]);
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: QuantitativeMetricRecordGridToolbar failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to set activity owner. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [user]);

  return (
    <>
      <GridToolbarContainer sx={{ my: 2, px: 0.5 }}>
        <Grid container spacing={0.2} sx={{ alignItems: 'center', p: 0, width: '100%' }}>
          <Grid size={12} sx={{ px: 1, mb: 3, display: 'flex' }}>
            <GridToolbarQuickFilter
              slotProps={{
                input: {
                  startAdornment: <SearchIcon height={15} style={{ fill: 'currentcolor' }} />
                }
              }}
              style={{ width: '80%' }}
            />
          </Grid>
          {capture_parameters.average_over_year === false && (
            <Grid flex={0.8}>
              <ReportingPeriodSelect
                selected_reporting_periods={reportingPeriod}
                reporting_period_options={reportingPeriodFilterByGroup(
                  capture_parameters.reporting_period_group?.id ?? '',
                  unlocked_reporting_periods
                )}
                input_label="Month"
                variant="outlined"
                clearable={false}
                handleChangeReportingPeriods={(
                  reporting_period: ReportingPeriod | Array<ReportingPeriod> | null
                ) => {
                  if (reporting_period && !Array.isArray(reporting_period)) {
                    handleChangeReportingPeriod(reporting_period);
                  }
                }}
              />
            </Grid>
          )}
          <Grid flex={capture_parameters.average_over_year ? 2 : 1.2}>
            {(() => {
              switch (capture_parameters.data_level) {
                case 'national':
                  return (
                    <CountrySelect
                      options={values<Country>(entities_map.countries)}
                      selected_options={
                        reportingEntity && 'code' in reportingEntity ? reportingEntity : null
                      }
                      variant="outlined"
                      hide_icons
                      clearable={false}
                      handleChangeCountries={(country: Country | Array<Country> | null) => {
                        if (country && !Array.isArray(country)) {
                          handleChangeReportingEntity(country);
                        }
                      }}
                    />
                  );
                case 'regional':
                  return (
                    <RegionSelect
                      options={values<Region>(entities_map.regions)}
                      selected_options={
                        reportingEntity && 'country' in reportingEntity ? reportingEntity : null
                      }
                      variant="outlined"
                      clearable={false}
                      group_by="country.name"
                      handleChangeRegions={(region: Region | Array<Region> | null) => {
                        if (region && !Array.isArray(region)) {
                          handleChangeReportingEntity(region);
                        }
                      }}
                    />
                  );
                case 'divisional':
                  return (
                    <DivisionSelect
                      options={values<Division>(entities_map.divisions)}
                      selected_options={reportingEntity}
                      variant="outlined"
                      clearable={false}
                      handleChangeDivisions={(division: Division | Array<Division> | null) => {
                        if (division && !Array.isArray(division)) {
                          handleChangeReportingEntity(division);
                        }
                      }}
                    />
                  );
                case 'subdivisional':
                  return (
                    <SubdivisionSelect
                      options={values<Subdivision>(entities_map.subdivisions)}
                      selected_options={
                        reportingEntity && 'division' in reportingEntity ? reportingEntity : null
                      }
                      variant="outlined"
                      clearable={false}
                      group_by="division.name"
                      handleChangeSubdivisions={(
                        subdivision: Subdivision | Array<Subdivision> | null
                      ) => {
                        if (subdivision && !Array.isArray(subdivision)) {
                          handleChangeReportingEntity(subdivision);
                        }
                      }}
                    />
                  );
                case 'site':
                  return (
                    <SiteSelect
                      options={values<Site>(entities_map.sites)}
                      selected_options={
                        reportingEntity && 'subdivision' in reportingEntity ? reportingEntity : null
                      }
                      variant="outlined"
                      group_by="subdivision.name"
                      clearable={false}
                      handleChangeSites={(site: Site | Array<Site> | null) => {
                        if (site && !Array.isArray(site)) {
                          handleChangeReportingEntity(site);
                        }
                      }}
                    />
                  );
                default:
                  return <></>;
              }
            })()}
          </Grid>
          <Grid flex={0.75}>
            <TextField
              id="value-input"
              fullWidth
              disabled={!reportingEntity}
              InputProps={{ inputProps: { min: 0 } }}
              label="Value"
              variant="outlined"
              sx={{ backgroundColor: 'white' }}
              value={value}
              onChange={handleChangeValue}
            />
          </Grid>
          <Grid flex={0.5} sx={{ textAlign: 'left', px: 0.5, wordWrap: 'break-word' }}>
            <CaptionText weight="Medium">
              {capture_parameters.metric ? capture_parameters.metric.unit : ''}
            </CaptionText>
          </Grid>
          {(user?.type === 'external' || activity_owner_options.length > 1) && (
            <Grid flex={1}>
              <TextField
                id="allocation-input"
                disabled={!value || !activityOwner?.is_external}
                type="number"
                fullWidth
                error={allocation < 0 || allocation > 100}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
                label="Client Allocation (%)"
                variant="outlined"
                sx={{ backgroundColor: 'white' }}
                value={allocation}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeAllocation(Number(event.target.value))
                }
              />
            </Grid>
          )}
          <Grid flex={1.2}>
            <TextField
              id="note-input"
              fullWidth
              disabled={!allocation}
              label="Note..."
              value={note}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeNote(event.target.value)
              }
              variant="outlined"
              sx={{ backgroundColor: 'white', width: '100%' }}
            />
          </Grid>
          {activity_owner_options.length > 1 && (
            <Grid flex={1.2}>
              <ActivityOwnerSelect
                options={activity_owner_options}
                selected_options={activityOwner}
                clearable={false}
                variant="outlined"
                handleChangeActivityOwner={(
                  activity_owner: ActivityOwner | Array<ActivityOwner> | null
                ) => {
                  if (!Array.isArray(activity_owner)) {
                    handleChangeActivityOwner(activity_owner);
                  }
                }}
              />
            </Grid>
          )}
          <Grid
            flex={activity_owner_options.length > 1 ? 0.8 : 1}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Tooltip title={`Add ${metric_record_label.one}`} arrow>
              <IconButton
                id="clear-inputs-button"
                sx={{
                  color: theme.palette.teal ? theme.palette.teal.dark : theme.palette.primary.main
                }}
                onClick={handleAddMetricRecord}
                disabled={!validateQuantitativeMetricRecord()}
                loading={addLoading}
              >
                <CreateIcon height={40} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear Inputs" key={2} arrow>
              <IconButton id="clear-inputs-button" onClick={resetInputs}>
                <BackspaceIcon
                  sx={{
                    color: theme.palette.teal ? theme.palette.teal.dark : theme.palette.primary.main
                  }}
                />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </GridToolbarContainer>
    </>
  );
};

export default QuantitativeMetricRecordGridToolbar;
